// 근무스케줄 신청 페이지 (사용자)
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  FormControl,
  FormLabel,
  Select,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  ButtonGroup,
  useDisclosure
} from '@chakra-ui/react';
import { ViewIcon, CalendarIcon } from '@chakra-ui/icons';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/ko';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { useAuth } from 'contexts/AuthContext';
import {
  getWorkSchedules,
  getUserScheduleApplications,
  getUserConfirmedSchedules,
  getActiveAcademicSchedule,
  getLocations,
  getUserSemesterWorkers,
  getAllSemesters
} from 'utils/firestoreUtils';
import { getDateRange, calculateEstimatedWage } from 'utils/scheduleUtils';
import ScheduleSlotCard from './components/ScheduleSlotCard';

moment.locale('ko');
const localizer = momentLocalizer(moment);

function ScheduleApplication() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');

  const [schedules, setSchedules] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [confirmedSchedules, setConfirmedSchedules] = useState([]);
  const [academicSchedule, setAcademicSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'calendar'
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [filters, setFilters] = useState({
    semesterId: '',
    location: '',
    startDate: '',
    endDate: ''
  });
  const [locations, setLocations] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  
  // 통계
  const [stats, setStats] = useState({
    totalHours: 0,
    estimatedWage: 0,
    confirmedCount: 0
  });

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  useEffect(() => {
    loadSchedules();
  }, [filters]);

  const loadData = async () => {
    try {
      const [locs, academic, apps, confirmed, userWorkers, allSemesters] = await Promise.all([
        getLocations(),
        getActiveAcademicSchedule(currentUser.uid),
        getUserScheduleApplications(currentUser.uid),
        getUserConfirmedSchedules(currentUser.uid),
        getUserSemesterWorkers(currentUser.uid),
        getAllSemesters()
      ]);

      // 근무자로 선정된 학기만 필터링
      const workerSemesterIds = userWorkers.map(w => w.semesterId);
      const filteredSemesters = allSemesters.filter(sem => workerSemesterIds.includes(sem.id));

      setLocations(locs);
      setSemesters(filteredSemesters);
      setAcademicSchedule(academic);
      setMyApplications(apps);
      setConfirmedSchedules(confirmed);

      // 통계 계산
      let totalMinutes = 0;
      let totalWage = 0;
      
      confirmed.forEach(item => {
        const schedule = item.scheduleDetails;
        if (schedule) {
          const wage = calculateEstimatedWage(
            schedule.wageAmount,
            schedule.startTime,
            schedule.endTime
          );
          totalWage += wage;
          
          const [startH, startM] = schedule.startTime.split(':').map(Number);
          const [endH, endM] = schedule.endTime.split(':').map(Number);
          const minutes = (endH * 60 + endM) - (startH * 60 + startM);
          totalMinutes += minutes;
        }
      });

      setStats({
        totalHours: (totalMinutes / 60).toFixed(1),
        estimatedWage: totalWage,
        confirmedCount: confirmed.length
      });
    } catch (error) {
      console.error('데이터 조회 오류:', error);
    }
  };

  const loadSchedules = async () => {
    // 학기가 선택되지 않으면 스케줄을 조회하지 않음
    if (!filters.semesterId) {
      setSchedules([]);
      return;
    }

    try {
      setLoading(true);
      const dateRange = filters.startDate && filters.endDate 
        ? { startDate: filters.startDate, endDate: filters.endDate }
        : getDateRange(0, 60);

      const data = await getWorkSchedules({
        ...dateRange,
        semesterId: filters.semesterId,
        location: filters.location || undefined,
        status: 'open'
      });

      setSchedules(data);
    } catch (error) {
      console.error('스케줄 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '스케줄을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationUpdate = () => {
    loadData();
    loadSchedules();
  };

  // 선택된 학기에 해당하는 신청 내역 필터링
  const filteredMyApplications = useMemo(() => {
    if (!filters.semesterId || !filters.startDate || !filters.endDate) {
      return [];
    }
    
    return myApplications.filter(app => {
      const workDate = app.scheduleDetails?.workDate;
      if (!workDate) return false;
      return workDate >= filters.startDate && workDate <= filters.endDate;
    });
  }, [myApplications, filters.semesterId, filters.startDate, filters.endDate]);

  // 선택된 학기에 해당하는 확정 스케줄 필터링
  const filteredConfirmedSchedules = useMemo(() => {
    if (!filters.semesterId || !filters.startDate || !filters.endDate) {
      return [];
    }
    
    return confirmedSchedules.filter(item => {
      const workDate = item.scheduleDetails?.workDate;
      if (!workDate) return false;
      return workDate >= filters.startDate && workDate <= filters.endDate;
    });
  }, [confirmedSchedules, filters.semesterId, filters.startDate, filters.endDate]);

  // 선택된 학기의 통계 계산
  const filteredStats = useMemo(() => {
    if (!filters.semesterId) {
      return {
        totalHours: 0,
        estimatedWage: 0,
        confirmedCount: 0
      };
    }

    let totalMinutes = 0;
    let totalWage = 0;
    
    filteredConfirmedSchedules.forEach(item => {
      const schedule = item.scheduleDetails;
      if (schedule) {
        const wage = calculateEstimatedWage(
          schedule.wageAmount,
          schedule.startTime,
          schedule.endTime
        );
        totalWage += wage;
        
        const [startH, startM] = schedule.startTime.split(':').map(Number);
        const [endH, endM] = schedule.endTime.split(':').map(Number);
        const minutes = (endH * 60 + endM) - (startH * 60 + startM);
        totalMinutes += minutes;
      }
    });

    return {
      totalHours: (totalMinutes / 60).toFixed(1),
      estimatedWage: totalWage,
      confirmedCount: filteredConfirmedSchedules.length
    };
  }, [filteredConfirmedSchedules, filters.semesterId]);

  // 스케줄을 캘린더 이벤트로 변환
  const schedulesToEvents = (schedulesList) => {
    return schedulesList.map(schedule => {
      const date = new Date(schedule.workDate);
      const [startHour, startMin] = schedule.startTime.split(':').map(Number);
      const [endHour, endMin] = schedule.endTime.split(':').map(Number);
      
      const start = new Date(date);
      start.setHours(startHour, startMin, 0);
      
      const end = new Date(date);
      end.setHours(endHour, endMin, 0);

      return {
        id: schedule.id,
        title: `${schedule.location} (${schedule.confirmedCount}/${schedule.requiredHeadcount})`,
        start,
        end,
        resource: schedule
      };
    });
  };

  const handleSelectEvent = (event) => {
    setSelectedSchedule(event.resource);
    onOpen();
  };

  const handleCloseModal = () => {
    setSelectedSchedule(null);
    onClose();
  };

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            근무스케줄 신청
          </Heading>
          <Text fontSize="sm" color="gray.500">
            {filters.semesterId ? (
              <>확정 {filteredStats.confirmedCount}개 | 총 {filteredStats.totalHours}시간 | 예상 임금 {filteredStats.estimatedWage.toLocaleString()}원</>
            ) : (
              <>학기를 선택하면 통계가 표시됩니다</>
            )}
          </Text>
        </Box>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button 
            leftIcon={<ViewIcon />}
            colorScheme={viewMode === 'card' ? 'blue' : 'gray'}
            onClick={() => setViewMode('card')}
          >
            카드
          </Button>
          <Button 
            leftIcon={<CalendarIcon />}
            colorScheme={viewMode === 'calendar' ? 'blue' : 'gray'}
            onClick={() => setViewMode('calendar')}
          >
            캘린더
          </Button>
        </ButtonGroup>
      </Flex>

      {/* 메인 컨텐츠 */}
      <Card flex="1" display="flex" flexDirection="column">
        <CardBody display="flex" flexDirection="column" flex="1">
          <Tabs variant="enclosed" colorScheme="blue" display="flex" flexDirection="column" flex="1">
            <TabList>
              <Tab>스케줄 조회 및 신청</Tab>
              <Tab>나의 신청 내역</Tab>
              <Tab>확정된 스케줄</Tab>
            </TabList>

            <TabPanels flex="1" display="flex" flexDirection="column">
              {/* Tab 1: 스케줄 조회 및 신청 */}
              <TabPanel px={0} flex="1" display="flex" flexDirection="column">
                {!academicSchedule && (
                  <Box mb={4} p={4} bg="yellow.50" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
                    <Text color="yellow.800" fontWeight="bold">알림</Text>
                    <Text color="yellow.700" fontSize="sm">
                      학업시간표를 등록하지 않았습니다. 
                      등록금재원 교내근로봉사에 참여하는 학생은 반드시 학업시간표를 등록하여야 합니다.
                    </Text>
                  </Box>
                )}

                {/* 필터 */}
                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
                  <FormControl>
                    <FormLabel fontSize="sm">학기 선택</FormLabel>
                    <Select
                      value={filters.semesterId}
                      onChange={(e) => {
                        const semester = semesters.find(s => s.id === e.target.value);
                        setSelectedSemester(semester);
                        setFilters({ 
                          semesterId: e.target.value,
                          location: '',
                          startDate: semester?.startDate || '',
                          endDate: semester?.endDate || ''
                        });
                      }}
                      placeholder="학기 선택"
                    >
                      {semesters.map(sem => (
                        <option key={sem.id} value={sem.id}>
                          {sem.semesterName} ({sem.startDate} ~ {sem.endDate})
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">근무지</FormLabel>
                    <Select
                      value={filters.location}
                      onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                      placeholder="전체 근무지"
                      isDisabled={!selectedSemester}
                    >
                      {selectedSemester?.workplaces?.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </Select>
                  </FormControl>

                  <Flex align="flex-end">
                    <Button onClick={loadSchedules} isLoading={loading} width="100%">
                      검색
                    </Button>
                  </Flex>
                </Grid>

                {/* 스케줄 목록 */}
                {!filters.semesterId ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500" fontSize="lg" fontWeight="bold" mb={2}>
                      학기를 선택해주세요
                    </Text>
                    <Text color="gray.400" fontSize="sm">
                      근무자로 등록된 학기를 선택하면 해당 학기의 근무 스케줄을 확인할 수 있습니다.
                    </Text>
                  </Box>
                ) : schedules.length === 0 ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500">선택한 학기에 신청 가능한 스케줄이 없습니다.</Text>
                  </Box>
                ) : viewMode === 'card' ? (
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                    {schedules.map(schedule => (
                      <ScheduleSlotCard
                        key={schedule.id}
                        schedule={schedule}
                        academicSchedule={academicSchedule}
                        myApplications={myApplications}
                        onUpdate={handleApplicationUpdate}
                      />
                    ))}
                  </Grid>
                ) : (
                  <Box 
                    flex="1"
                    minHeight="500px"
                    width="100%"
                    sx={{
                      '.rbc-calendar': {
                        height: '100%',
                        fontFamily: 'inherit'
                      },
                      '.rbc-header': {
                        padding: '10px 3px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        borderBottom: '2px solid',
                        borderColor: borderColor
                      },
                      '.rbc-today': {
                        backgroundColor: useColorModeValue('blue.50', 'blue.900')
                      },
                      '.rbc-event': {
                        padding: '4px 6px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: '#3182ce'
                      },
                      '.rbc-event:hover': {
                        opacity: 0.9,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      },
                      '.rbc-toolbar': {
                        padding: '15px 0',
                        marginBottom: '15px',
                        flexWrap: 'wrap',
                        gap: '10px'
                      },
                      '.rbc-toolbar button': {
                        color: textColor,
                        padding: '8px 15px',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: borderColor,
                        backgroundColor: 'white',
                        fontSize: '14px',
                        fontWeight: '500'
                      },
                      '.rbc-toolbar button:hover': {
                        backgroundColor: useColorModeValue('gray.100', 'gray.700')
                      },
                      '.rbc-toolbar button.rbc-active': {
                        backgroundColor: 'blue.500',
                        color: 'white',
                        borderColor: 'blue.500'
                      },
                      '.rbc-month-view': {
                        border: '1px solid',
                        borderColor: borderColor,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        height: '100%'
                      },
                      '.rbc-month-row': {
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1 0 0',
                        flexBasis: '0px',
                        overflow: 'hidden',
                        minHeight: '100px'
                      },
                      '.rbc-row-content': {
                        position: 'relative',
                        userSelect: 'none',
                        zIndex: 4,
                        minHeight: '50px'
                      },
                      '.rbc-row': {
                        display: 'flex',
                        flexDirection: 'row'
                      },
                      '.rbc-day-bg': {
                        borderColor: borderColor,
                        flex: '1 0 0%'
                      },
                      '.rbc-off-range-bg': {
                        backgroundColor: useColorModeValue('gray.50', 'gray.800')
                      },
                      '.rbc-date-cell': {
                        padding: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        textAlign: 'right'
                      },
                      '.rbc-show-more': {
                        color: 'blue.500',
                        fontWeight: '600',
                        fontSize: '12px',
                        marginTop: '4px'
                      }
                    }}
                  >
                    <Calendar
                      localizer={localizer}
                      events={schedulesToEvents(schedules)}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%', width: '100%' }}
                      onSelectEvent={handleSelectEvent}
                      views={['month', 'week', 'day']}
                      defaultView="month"
                      messages={{
                        next: '다음',
                        previous: '이전',
                        today: '오늘',
                        month: '월간',
                        week: '주간',
                        day: '일간',
                        showMore: (total) => `+${total} 더보기`
                      }}
                    />
                  </Box>
                )}
              </TabPanel>

              {/* Tab 2: 나의 신청 내역 */}
              <TabPanel px={0} flex="1" display="flex" flexDirection="column">
                {!filters.semesterId ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500" fontSize="lg" fontWeight="bold" mb={2}>
                      학기를 선택해주세요
                    </Text>
                    <Text color="gray.400" fontSize="sm">
                      근무자로 등록된 학기를 선택하면 해당 학기의 신청 내역을 확인할 수 있습니다.
                    </Text>
                  </Box>
                ) : filteredMyApplications.length === 0 ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500">선택한 학기에 신청 내역이 없습니다.</Text>
                  </Box>
                ) : viewMode === 'card' ? (
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                    {filteredMyApplications.map(app => (
                      <ScheduleSlotCard
                        key={app.id}
                        schedule={null}
                        application={app}
                        myApplications={myApplications}
                        onUpdate={handleApplicationUpdate}
                        isMyApplication={true}
                      />
                    ))}
                  </Grid>
                ) : (
                  <Box 
                    flex="1"
                    minHeight="500px"
                    width="100%"
                    sx={{
                      '.rbc-calendar': {
                        height: '100%',
                        fontFamily: 'inherit'
                      },
                      '.rbc-header': {
                        padding: '10px 3px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        borderBottom: '2px solid',
                        borderColor: borderColor
                      },
                      '.rbc-today': {
                        backgroundColor: useColorModeValue('blue.50', 'blue.900')
                      },
                      '.rbc-event': {
                        padding: '4px 6px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: '#ed8936'
                      },
                      '.rbc-event:hover': {
                        opacity: 0.9,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      },
                      '.rbc-toolbar': {
                        padding: '15px 0',
                        marginBottom: '15px',
                        flexWrap: 'wrap',
                        gap: '10px'
                      },
                      '.rbc-toolbar button': {
                        color: textColor,
                        padding: '8px 15px',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: borderColor,
                        backgroundColor: 'white',
                        fontSize: '14px',
                        fontWeight: '500'
                      },
                      '.rbc-toolbar button:hover': {
                        backgroundColor: useColorModeValue('gray.100', 'gray.700')
                      },
                      '.rbc-toolbar button.rbc-active': {
                        backgroundColor: 'blue.500',
                        color: 'white',
                        borderColor: 'blue.500'
                      },
                      '.rbc-month-view': {
                        border: '1px solid',
                        borderColor: borderColor,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        height: '100%'
                      },
                      '.rbc-month-row': {
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1 0 0',
                        flexBasis: '0px',
                        overflow: 'hidden',
                        minHeight: '80px'
                      },
                      '.rbc-row-content': {
                        position: 'relative',
                        userSelect: 'none',
                        zIndex: 4,
                        minHeight: '50px'
                      },
                      '.rbc-row': {
                        display: 'flex',
                        flexDirection: 'row'
                      },
                      '.rbc-day-bg': {
                        borderColor: borderColor,
                        flex: '1 0 0%'
                      },
                      '.rbc-off-range-bg': {
                        backgroundColor: useColorModeValue('gray.50', 'gray.800')
                      },
                      '.rbc-date-cell': {
                        padding: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        textAlign: 'right'
                      },
                      '.rbc-show-more': {
                        color: 'blue.500',
                        fontWeight: '600',
                        fontSize: '12px',
                        marginTop: '4px'
                      }
                    }}
                  >
                    <Calendar
                      localizer={localizer}
                      events={schedulesToEvents(filteredMyApplications.map(a => a.scheduleDetails).filter(Boolean))}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%', width: '100%' }}
                      onSelectEvent={(event) => {
                        const app = filteredMyApplications.find(a => a.scheduleId === event.id);
                        if (app) {
                          setSelectedSchedule(app.scheduleDetails);
                          onOpen();
                        }
                      }}
                      views={['month', 'week', 'day']}
                      defaultView="month"
                      messages={{
                        next: '다음',
                        previous: '이전',
                        today: '오늘',
                        month: '월간',
                        week: '주간',
                        day: '일간',
                        showMore: (total) => `+${total} 더보기`
                      }}
                    />
                  </Box>
                )}
              </TabPanel>

              {/* Tab 3: 확정된 스케줄 */}
              <TabPanel px={0} flex="1" display="flex" flexDirection="column">
                {filters.semesterId && (
                  <>
                    {/* 통계 */}
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
                      <Card>
                        <CardBody>
                          <Stat>
                            <StatLabel>확정 근무</StatLabel>
                            <StatNumber>{filteredStats.confirmedCount}건</StatNumber>
                            <StatHelpText>선정된 스케줄</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <Stat>
                            <StatLabel>총 근무시간</StatLabel>
                            <StatNumber>{filteredStats.totalHours}시간</StatNumber>
                            <StatHelpText>예정 근무시간</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <Stat>
                            <StatLabel>예상 급여</StatLabel>
                            <StatNumber>{filteredStats.estimatedWage.toLocaleString()}원</StatNumber>
                            <StatHelpText>세전 금액</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </Grid>
                  </>
                )}

                {!filters.semesterId ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500" fontSize="lg" fontWeight="bold" mb={2}>
                      학기를 선택해주세요
                    </Text>
                    <Text color="gray.400" fontSize="sm">
                      근무자로 등록된 학기를 선택하면 해당 학기의 확정 스케줄을 확인할 수 있습니다.
                    </Text>
                  </Box>
                ) : filteredConfirmedSchedules.length === 0 ? (
                  <Box textAlign="center" py={10}>
                    <Text color="gray.500">선택한 학기에 확정된 스케줄이 없습니다.</Text>
                  </Box>
                ) : viewMode === 'card' ? (
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                    {filteredConfirmedSchedules.map(item => (
                      <ScheduleSlotCard
                        key={item.id}
                        schedule={item.scheduleDetails}
                        application={item}
                        myApplications={myApplications}
                        onUpdate={handleApplicationUpdate}
                        isConfirmed={true}
                      />
                    ))}
                  </Grid>
                ) : (
                  <Box 
                    flex="1"
                    minHeight="400px"
                    width="100%"
                    sx={{
                      '.rbc-calendar': {
                        height: '100%',
                        fontFamily: 'inherit'
                      },
                      '.rbc-header': {
                        padding: '10px 3px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        borderBottom: '2px solid',
                        borderColor: borderColor
                      },
                      '.rbc-today': {
                        backgroundColor: useColorModeValue('blue.50', 'blue.900')
                      },
                      '.rbc-event': {
                        padding: '4px 6px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: '#38a169'
                      },
                      '.rbc-event:hover': {
                        opacity: 0.9,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      },
                      '.rbc-toolbar': {
                        padding: '15px 0',
                        marginBottom: '15px',
                        flexWrap: 'wrap',
                        gap: '10px'
                      },
                      '.rbc-toolbar button': {
                        color: textColor,
                        padding: '8px 15px',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: borderColor,
                        backgroundColor: 'white',
                        fontSize: '14px',
                        fontWeight: '500'
                      },
                      '.rbc-toolbar button:hover': {
                        backgroundColor: useColorModeValue('gray.100', 'gray.700')
                      },
                      '.rbc-toolbar button.rbc-active': {
                        backgroundColor: 'blue.500',
                        color: 'white',
                        borderColor: 'blue.500'
                      },
                      '.rbc-month-view': {
                        border: '1px solid',
                        borderColor: borderColor,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        height: '100%'
                      },
                      '.rbc-month-row': {
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1 0 0',
                        flexBasis: '0px',
                        overflow: 'hidden',
                        minHeight: '80px'
                      },
                      '.rbc-row-content': {
                        position: 'relative',
                        userSelect: 'none',
                        zIndex: 4,
                        minHeight: '50px'
                      },
                      '.rbc-row': {
                        display: 'flex',
                        flexDirection: 'row'
                      },
                      '.rbc-day-bg': {
                        borderColor: borderColor,
                        flex: '1 0 0%'
                      },
                      '.rbc-off-range-bg': {
                        backgroundColor: useColorModeValue('gray.50', 'gray.800')
                      },
                      '.rbc-date-cell': {
                        padding: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        textAlign: 'right'
                      },
                      '.rbc-show-more': {
                        color: 'blue.500',
                        fontWeight: '600',
                        fontSize: '12px',
                        marginTop: '4px'
                      }
                    }}
                  >
                    <Calendar
                      localizer={localizer}
                      events={schedulesToEvents(filteredConfirmedSchedules.map(c => c.scheduleDetails).filter(Boolean))}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%', width: '100%' }}
                      onSelectEvent={(event) => {
                        const conf = filteredConfirmedSchedules.find(c => c.scheduleDetails?.id === event.id);
                        if (conf) {
                          setSelectedSchedule(conf.scheduleDetails);
                          onOpen();
                        }
                      }}
                      views={['month', 'week', 'day']}
                      defaultView="month"
                      messages={{
                        next: '다음',
                        previous: '이전',
                        today: '오늘',
                        month: '월간',
                        week: '주간',
                        day: '일간',
                        showMore: (total) => `+${total} 더보기`
                      }}
                    />
                  </Box>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      {/* 스케줄 상세 모달 */}
      {selectedSchedule && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.600"
          zIndex="1000"
          display={isOpen ? 'flex' : 'none'}
          alignItems="center"
          justifyContent="center"
          onClick={handleCloseModal}
        >
          <Box
            bg="white"
            borderRadius="lg"
            p={6}
            maxW="600px"
            width="90%"
            maxH="80vh"
            overflowY="auto"
            onClick={(e) => e.stopPropagation()}
          >
            <ScheduleSlotCard
              schedule={selectedSchedule}
              academicSchedule={academicSchedule}
              myApplications={myApplications}
              onUpdate={() => {
                handleApplicationUpdate();
                handleCloseModal();
              }}
            />
            <Button 
              mt={4} 
              width="100%" 
              onClick={handleCloseModal}
            >
              닫기
            </Button>
          </Box>
        </Box>
      )}
    </Flex>
  );
}

export default ScheduleApplication;
